import type { ReactNode } from 'react'
import cx from 'classnames'
import { m, useTransform, type MotionValue } from 'motion/react'
import classicStyles from './StickyViewHeader.module.css'
import refreshStyles from './StickyViewHeader.refresh.module.css'
import {
  AnimationProgressProvider,
  useAnimationProgress,
} from '@/contexts/animation-progress-context'
import { clamp, lerp } from '@/utils/math-util'
import { useFallbackMotionValue } from '@/hooks/motion-hooks'
import { ScrollShadow } from '@/components/ScrollShadow'
import { isRefreshDesign } from '@/infra/design'

const styles = isRefreshDesign ? refreshStyles : classicStyles

interface StickyViewHeaderProps {
  /**
   * Drives the classic header's compaction on scroll. The refreshed design's
   * header is a fixed-height row with a permanent hairline and ignores it.
   */
  scrollOffset?: MotionValue<number>
  children?: ReactNode
  bordered?: boolean
  /**
   * If specified, must be a TabBar component.
   */
  tabBar?: ReactNode
  /**
   * If true, the header will not have top padding.
   */
  noTopPadding?: boolean
  /**
   * The refreshed design only: the header sits directly on full-bleed
   * content, with a tight gutter and no gap below.
   */
  flush?: boolean
}

/**
 * How much to move the header up
 */
const DELTA = 49

function ClassicStickyViewHeader({
  children,
  scrollOffset,
  tabBar,
  bordered,
  noTopPadding,
}: StickyViewHeaderProps) {
  const offset = useFallbackMotionValue(scrollOffset)
  const compactionAnimationProgress = useTransform(offset, (v) =>
    clamp(v / DELTA),
  )
  const shadowAnimationProgress = useTransform(offset, (v) =>
    // Makes the shadow animation start a bit later.
    clamp(v / DELTA - 0.75),
  )
  const borderOpacity = useTransform(shadowAnimationProgress, (v) => 1 - v)

  const rootStyle = {
    transform: useTransform(
      compactionAnimationProgress,
      (v) => `translateY(${lerp(0, noTopPadding ? -16 : -49, v)}px)`,
    ),
  }
  const contentStyle = {
    transform: useTransform(
      compactionAnimationProgress,
      (v) => `translateY(${lerp(0, 16, v)}px)`,
    ),
  }
  return (
    <AnimationProgressProvider progress={compactionAnimationProgress}>
      <m.div className={styles.root} style={rootStyle}>
        <div className={cx(styles.pad, noTopPadding && styles.noTopPadding)}>
          <m.div className={styles.content} style={contentStyle}>
            {children}
          </m.div>
        </div>
        {tabBar && <div className={styles.tabBar}>{tabBar}</div>}
        {(tabBar || bordered) && (
          <m.div style={{ opacity: borderOpacity }} className={styles.border} />
        )}
        <ScrollShadow opacity={shadowAnimationProgress} />
      </m.div>
    </AnimationProgressProvider>
  )
}

function FixedStickyViewHeader({
  children,
  tabBar,
  noTopPadding,
  flush,
}: StickyViewHeaderProps) {
  return (
    <div className={cx(styles.root, flush && styles.flush)}>
      <div className={cx(styles.pad, noTopPadding && styles.noTopPadding)}>
        <div className={styles.content}>{children}</div>
      </div>
      {tabBar && <div className={styles.tabBar}>{tabBar}</div>}
    </div>
  )
}

/**
 * A view header that stays at the top while the view scrolls.
 */
export const StickyViewHeader = isRefreshDesign
  ? FixedStickyViewHeader
  : ClassicStickyViewHeader

export interface StickyViewHeaderMainProps {
  children?: ReactNode
}

/**
 * The main content of a sticky view header.
 */
export function StickyViewHeaderMain({ children }: StickyViewHeaderMainProps) {
  return <div className={styles.main}>{children}</div>
}

export interface StickyViewHeaderSpacerProps {
  children?: ReactNode
}

/**
 * Pushes the following sticky view header content to the end.
 */
export function StickyViewHeaderSpacer({
  children,
}: StickyViewHeaderSpacerProps) {
  return <div className={styles.spacer}>{children}</div>
}

export interface StickyViewHeaderExtrasProps {
  children?: ReactNode
}

/**
 * Extra content in a sticky view header.
 */
export function StickyViewHeaderExtras({
  children,
}: StickyViewHeaderExtrasProps) {
  return isRefreshDesign ? (
    <div className={styles.extras}>{children}</div>
  ) : (
    <AnimatedExtras>{children}</AnimatedExtras>
  )
}

function AnimatedExtras({ children }: StickyViewHeaderExtrasProps) {
  const progress = useAnimationProgress()
  const translate = useTransform(
    progress,
    (v) => `translateY(${lerp(0, 8, v)}px)`,
  )
  return (
    <m.div style={{ transform: translate }} className={styles.extras}>
      {children}
    </m.div>
  )
}
